import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTRPC } from "@dmca/frontend/trpc";
import {
  Typography,
  Container,
  Paper,
  Box,
  IconButton,
  Stack,
  Alert,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import {
  Select,
  type SelectChangeEvent,
  Input,
} from "@dmca/frontend/components/input";
import { Button } from "@dmca/frontend/components/button";
import { toast } from "react-toastify";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  ValidCognitoPermissionGroupsNameMap,
  ValidCognitoPermissionGroupsEnum,
} from "@dmca/models/interface/auth";
import {
  checkPermissionsInLoader,
  useAppPermissions,
  useUser,
} from "@dmca/frontend/hooks";
import { PageHeader } from "@dmca/frontend/components/nav";

export const Route = createFileRoute("/_authed/admin/invite-user")({
  component: GenerateSignUpCode,
  loader: async ({ context }) =>
    await checkPermissionsInLoader("Admin::InviteUser", context),
  head: () => ({
    meta: [
      {
        title: "Invite Users | DNA Dashboard",
      },
    ],
  }),
});

function GenerateSignUpCode() {
  const [accountTypeValue, setAccountTypeValue] =
    useState<ValidCognitoPermissionGroupsEnum | null>(null);
  const [emailFields, setEmailFields] = useState<string[]>([""]);
  const [errorMessage, setErrorMessage] = useState("");

  const user = useUser();
  const canManageAllUsers = useAppPermissions()("Admin::UpdateUser");

  const trpc = useTRPC();
  const createSignupCodeMutation = useMutation(
    trpc.user.create.mutationOptions({
      onSuccess: () => {
        toast.success(
          `Account creation emails sent to ${emailFields.length} recipient(s)`
        );
        // Reset form
        setErrorMessage("");
        setEmailFields([""]);
      },
      onError: (error) => {
        console.error("Error sending invitations:", error);
        if (error.data?.code === "BAD_REQUEST") {
          setErrorMessage(error.message || "Invalid or duplicate email(s).");
        } else {
          setErrorMessage("");
          toast.error(
            "An unknown error occurred. Please try again or contact support."
          );
        }
      },
    })
  );

  const handleChangeSelect = (e: SelectChangeEvent) => {
    setAccountTypeValue(e.target.value as ValidCognitoPermissionGroupsEnum);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmailFields = [...emailFields];
    newEmailFields[index] = value;
    setEmailFields(newEmailFields);
  };

  const addEmailField = () => {
    setEmailFields([...emailFields, ""]);
  };

  const removeEmailField = (index: number) => {
    if (emailFields.length > 1) {
      setEmailFields(emailFields.filter((_, i) => i !== index));
    }
  };

  const handleSendInvitations = () => {
    if (!accountTypeValue || !user) {
      toast.error("Please select an account type");
      return;
    }

    // Filter out empty fields and get unique emails
    const emailsToSend = emailFields
      .filter((email) => email.trim() !== "")
      .filter((email, index, self) => self.indexOf(email) === index);

    if (emailsToSend.length === 0) {
      toast.error("Please enter at least one email address");
      return;
    }

    // Validate all emails
    const invalidEmails = emailsToSend.filter((email) => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email addresses: ${invalidEmails.join(", ")}`);
      return;
    }

    createSignupCodeMutation.mutate({
      assignedGroup: accountTypeValue,
      emails: emailsToSend,
      callbackURL: window.location.origin + "/create-account",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendInvitations();
  };

  return (
    <>
      <PageHeader>Invite Users</PageHeader>
      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "80vh",
        }}
      >
        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            padding: 3,
            width: "100%",
            "& > *": {
              my: 2,
            },
          }}
        >
          <Typography variant="h5" gutterBottom>
            Send Invitations
          </Typography>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Select
            label="Account Type"
            value={accountTypeValue}
            options={Object.entries(ValidCognitoPermissionGroupsNameMap)
              .filter(
                ([key]) =>
                  key !== ValidCognitoPermissionGroupsEnum.Admin ||
                  canManageAllUsers
              )
              .map(([key, val]) => ({
                name: val,
                value: key,
              }))}
            allowNull={false}
            onChange={handleChangeSelect}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Email Addresses
            </Typography>

            {emailFields.map((email, index) => (
              <Stack key={index} direction="row" alignItems="center">
                <Input
                  label={`Email Address ${
                    emailFields.length > 1 ? index + 1 : ""
                  }`}
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  placeholder="Enter email address"
                  error={!!email && !isValidEmail(email)}
                  helperText={
                    email && !isValidEmail(email)
                      ? "Please enter a valid email address"
                      : ""
                  }
                />
                {emailFields.length > 1 && (
                  <IconButton
                    onClick={() => removeEmailField(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            ))}

            <Button
              variant="outlined"
              onClick={addEmailField}
              startIcon={<AddIcon />}
              sx={{ alignSelf: "flex-start" }}
            >
              Add Another Email
            </Button>
          </Box>

          <Button
            variant="contained"
            size="large"
            type="submit"
            disabled={!accountTypeValue || createSignupCodeMutation.isPending}
            loading={createSignupCodeMutation.isPending}
            fullWidth
          >
            Send Invitation Email
            {emailFields.filter((e) => e.trim()).length > 1 ? "s" : ""}
          </Button>
        </Paper>
      </Container>
    </>
  );
}
